import { Box, Typography, styled } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import React, { useContext, useEffect, useState } from 'react';
import FOOD from '../../../assets/image/food.png';
import db_config from '../../../configs/db_config';
import { ROLES } from '../../../consts/language';
import { useAuthContext } from '../../../context/AuthContext/AuthContext';
import { NewOrderAlertsContext } from '../../../context/NewOrderAlerts';
import { useSettingsContext } from '../../../context/SettingsContext';
import { useSidebarItems } from '../../../hooks/useSidebarItems';
import { db } from '../../../libs/firebase';
import { doc, getDoc, updateDoc } from '../../../libs/firestore-wrapper';
import { SidebarItemComponent } from './SidebarItemComponent';
import { useStyles } from './styles';

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflow: 'hidden',
      width: '82px',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    }),
  },
}));

function SideBar() {
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const { logoutUser, userInfo } = useAuthContext();
  const { currentSetting, loading } = useSettingsContext();
  let { deviceToken } = useContext(NewOrderAlertsContext);

  async function handleLogoutButtonOnClick() {
    logoutUser();
    if (userInfo?.role === ROLES.STAFF && userInfo?.restaurant_id) {
      (async () => {
        try {
          const userDocRef = doc(db, db_config.DB_TABLE_RESTAURANT_CONFIG, userInfo.restaurant_id);
          const userDoc = await getDoc(userDocRef);
          const deviceTokens = userDoc.data()?.device_tokens || [];
          const updatedDeviceTokens = deviceTokens.filter((token: string) => token !== deviceToken);
          await updateDoc(userDocRef, { device_tokens: updatedDeviceTokens });
        } catch (err) {
          console.error('Failed to remove device token after logout', err);
        }
      })();
    }
  }

  const { sidebarItems, bottomItems, staffSidebarItems } = useSidebarItems({
    handleLogoutButtonOnClick,
  });

  useEffect(() => {
    function handleResize() {
      setOpen(window.innerWidth >= 600);
    }
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setExpandedItems({});
    }
  }, [open]);

  const toggleSubMenu = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  return (
    <Drawer variant="permanent" open={open} sx={{ width: open ? 280 : 82 }}>
      <List component="nav" className={classes.ListMenu}>
        <Box className={`${classes.BoxMenu}`}>
          {userInfo.role === ROLES.STAFF && (
            <UserInfoDisplay loading={loading} profilePic={currentSetting.restaurant_profile_pic} name={currentSetting.name} open={open} classes={classes} />
          )}
          {userInfo.role === ROLES.SUPER_ADMIN || userInfo.role === ROLES.HEADQUARTER && (
            <UserInfoDisplay loading={false} profilePic={userInfo.stadium_profile_pic} name={userInfo.name} open={open} classes={classes} />
          )}
          <Box className={`${classes.BoxStyles}`}>
            {[...staffSidebarItems, ...sidebarItems].map((item) => (
              <SidebarItemComponent key={item.id} item={item} open={open} classes={classes} isExpanded={expandedItems[item.id]} toggleSubMenu={toggleSubMenu} />
            ))}
          </Box>
        </Box>
        <Box className={classes.BoxMenu}>
          <Box className={classes.BoxStyles}>
            {bottomItems.map((item) => (
              <SidebarItemComponent key={item.id} item={item} open={open} classes={classes} isExpanded={expandedItems[item.id]} toggleSubMenu={toggleSubMenu} />
            ))}
          </Box>
        </Box>
      </List>
    </Drawer>
  );
}

const UserInfoDisplay = ({ loading, profilePic, name, open, classes }: { loading: boolean; profilePic: string; name: string; open: boolean; classes: any }) => {
  return (
    <Box className={classes.BoxImage}>
      {!loading && profilePic && <img src={`${profilePic}`} alt={FOOD} loading="lazy" className={classes.Logo} />}
      {open && (
        <Typography fontSize={14} lineHeight={'20px'} color={'var(--Primary-200, #C6ECCA)'}>
          {!loading && name}
        </Typography>
      )}
    </Box>
  );
};

export default SideBar;
